/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import { API_BASE_URL, HTTP_STATUS, TOKEN_KEYS } from '../../configs';
import {
  getItemDataStorage,
  removeItemDataStorage,
} from '../utils/localStorage';
import { AuthApi } from './auth';
import { MemberApi } from './members';
import { SeminarApi } from './seminars';
import { ParticipantApi } from './participants';
import { AreaApi } from './areas';
import { NotificationApi } from './notifications';
import { AdminUserApi } from './admin-users';
import { SafetyApi } from './safety';
import { QuestionnaireApi } from './questionnaires';
import { NewsApi } from './news';

class Api {
  instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
    });

    this.instance.interceptors.request.use(
      this.onRequestFulfilled,
      this.onRequestReject,
    );

    this.instance.interceptors.response.use(
      this.onResponseFulfilled,
      this.onResponseReject,
    );
  }

  onResponseFulfilled = (response: AxiosResponse): AxiosResponse => {
    return response;
  };

  onResponseReject = async (error: AxiosError) => {
    if (error.response) {
      switch (error.response?.status) {
        case HTTP_STATUS.UNAUTHORIZED: {
          removeItemDataStorage(TOKEN_KEYS.ACCESS_TOKEN);
          removeItemDataStorage(TOKEN_KEYS.EXPIRES_IN);
          // eslint-disable-next-line no-undef
          window.location.href = '/login';
          break;
        }
        case HTTP_STATUS.FORBIDDEN: // navigate to page 403
          break;
        default:
          throw {
            statusCode: error.response.status,
            message: this.getErrorMessage(error),
          };
      }
    } else {
      throw error;
    }
  };

  onRequestFulfilled = async (
    config: AxiosRequestConfig,
  ): Promise<AxiosRequestConfig> => {
    const accessToken = getItemDataStorage(TOKEN_KEYS.ACCESS_TOKEN);
    if (accessToken) {
      const bearerToken = `Bearer ${accessToken}`;
      config.headers = {
        ...(config.headers ?? {}),
        ['Authorization']: bearerToken,
      };
    }
    return config;
  };

  onRequestReject = (error: any) => {
    return Promise.reject(error);
  };

  // TODO: Get message from error's response
  getErrorMessage = (error: AxiosError) => {
    if (error.response && error.response.data) {
      if (Array.isArray(error.response.data)) {
        return error.response.data;
      }
      return error.response.data;
    }
    return 'An error has occurred, please try again';
  };
}

const { instance } = new Api();

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace apiSdk {
  export const authApis = new AuthApi(instance);
  export const areaApis = new AreaApi(instance);
  export const memberApis = new MemberApi(instance);
  export const notificationApis = new NotificationApi(instance);
  export const newsApis = new NewsApi(instance);
  export const safetyApis = new SafetyApi(instance);
  export const seminarApis = new SeminarApi(instance);
  export const participantApis = new ParticipantApi(instance);
  export const adminUserApis = new AdminUserApi(instance);
  export const questionnaireApis = new QuestionnaireApi(instance);
}
